/*
 * Cart experience glue:
 *  - keeps a live copy of the cart (/cart.js) and shares it with every product card
 *  - <spx-card-add>: turns a card's ADD button into a "- 1 +" stepper once the item is in the cart
 *  - floating cart button (count badge) that opens the side cart
 *  - "Added to cart" popup hides itself after a couple of seconds; its cart link opens the side cart
 *  - the side cart is refreshed every time it is opened
 */
(() => {
  if (window.__spxCartUi) return;
  window.__spxCartUi = true;

  const cartUrl = () => (window.routes && window.routes.cart_url) || '/cart';
  const changeUrl = () => (window.routes && window.routes.cart_change_url) || '/cart/change';
  const cartState = { count: 0, lines: new Map(), products: new Map(), signature: '' };
  let drawerSignature = null;

  const applyCart = (cart) => {
    cartState.count = cart.item_count;
    cartState.lines = new Map(cart.items.map((item) => [String(item.variant_id), { key: item.key, quantity: item.quantity }]));
    cartState.products = new Map();
    cart.items.forEach((item) => {
      const id = String(item.product_id);
      cartState.products.set(id, (cartState.products.get(id) || 0) + item.quantity);
    });
    cartState.signature = cart.items.map((item) => `${item.key}:${item.quantity}`).join('|');
    document.dispatchEvent(new CustomEvent('spx:cart', { detail: cartState }));
  };

  const refresh = () =>
    fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then(applyCart)
      .catch(() => {});

  const refreshHeaderBubble = () =>
    fetch(`${cartUrl()}?section_id=cart-icon-bubble`)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('.shopify-section');
        const bubble = document.getElementById('cart-icon-bubble');
        if (fresh && bubble) bubble.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});

  /* ---------- floating cart button count ---------- */
  document.addEventListener('spx:cart', () => {
    document.querySelectorAll('[data-spx-cart-count]').forEach((element) => {
      element.textContent = cartState.count;
      element.hidden = cartState.count === 0;
    });
  });

  /* ---------- side cart ---------- */
  const refreshDrawer = async (drawer) => {
    try {
      const response = await fetch(`${cartUrl()}?section_id=cart-drawer`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('#CartDrawer');
      const current = drawer.querySelector('#CartDrawer');
      if (!fresh || !current) return;
      current.innerHTML = fresh.innerHTML;
      const freshDrawer = doc.querySelector('cart-drawer');
      drawer.classList.toggle('is-empty', Boolean(freshDrawer && freshDrawer.classList.contains('is-empty')));
      const overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', () => drawer.close());
    } catch (error) {
      /* the drawer keeps its last content */
    }
  };

  let drawerTimer;
  document.addEventListener('spx:cart', () => {
    if (drawerSignature === null) {
      drawerSignature = cartState.signature;
      return;
    }
    if (drawerSignature === cartState.signature) return;
    drawerSignature = cartState.signature;
    clearTimeout(drawerTimer);
    drawerTimer = setTimeout(() => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) refreshDrawer(drawer);
    }, 250);
  });

  const openDrawer = (trigger) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) {
      window.location.href = cartUrl();
      return;
    }
    drawer.open(trigger);
  };

  customElements.whenDefined('cart-drawer').then(() => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.dataset.spxPatched) return;
    drawer.dataset.spxPatched = 'true';
    const nativeOpen = drawer.open.bind(drawer);
    drawer.open = (trigger) => {
      refreshDrawer(drawer);
      nativeOpen(trigger);
    };
  });

  document.addEventListener('click', (event) => {
    const fab = event.target.closest('[data-spx-fab]');
    if (!fab || !document.querySelector('cart-drawer')) return;
    event.preventDefault();
    openDrawer(fab);
  });

  /* ---------- "Added to cart" popup ---------- */
  customElements.whenDefined('cart-notification').then(() => {
    const host = document.querySelector('cart-notification');
    const panel = document.getElementById('cart-notification');
    if (!host || !panel) return;

    let timer;
    const stop = () => clearTimeout(timer);
    const start = (delay) => {
      stop();
      timer = setTimeout(() => host.close(), delay);
    };

    new MutationObserver(() => {
      if (panel.classList.contains('active')) start(2600);
      else stop();
    }).observe(panel, { attributes: true, attributeFilter: ['class'] });

    panel.addEventListener('mouseenter', stop);
    panel.addEventListener('mouseleave', () => {
      if (panel.classList.contains('active')) start(1200);
    });
    panel.addEventListener('focusin', (event) => {
      if (event.target !== panel) stop();
    });

    host.addEventListener('click', (event) => {
      const link = event.target.closest('#cart-notification-button');
      if (!link || !document.querySelector('cart-drawer')) return;
      event.preventDefault();
      stop();
      host.close();
      openDrawer(link);
    });
  });

  /* ---------- ADD -> stepper on product cards ---------- */
  if (!customElements.get('spx-card-add')) {
    customElements.define(
      'spx-card-add',
      class SpxCardAdd extends HTMLElement {
        connectedCallback() {
          this.variantId = this.dataset.variantId;
          this.stepper = this.querySelector('[data-stepper]');
          this.quantity = this.querySelector('[data-qty]');
          this.addWrapper = this.querySelector('[data-add-wrapper]');
          this.busy = false;

          this.onCart = () => this.render();
          document.addEventListener('spx:cart', this.onCart);
          this.stepper.addEventListener('click', (event) => {
            const button = event.target.closest('[data-step]');
            if (button) this.change(Number(button.dataset.step));
          });
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener('spx:cart', this.onCart);
        }

        render() {
          const line = cartState.lines.get(this.variantId);
          const quantity = line ? line.quantity : 0;
          this.stepper.hidden = quantity === 0;
          this.addWrapper.hidden = quantity > 0;
          if (quantity > 0) this.quantity.textContent = quantity;
        }

        async change(delta) {
          if (this.busy) return;
          const line = cartState.lines.get(this.variantId);
          const next = Math.max(0, (line ? line.quantity : 0) + delta);
          this.busy = true;
          this.stepper.setAttribute('aria-busy', 'true');

          try {
            const response = await fetch(`${changeUrl()}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: this.variantId, quantity: next })
            });
            const cart = await response.json();
            if (cart.items) {
              applyCart(cart);
              refreshHeaderBubble();
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'spx-card', cartData: cart });
              }
            }
          } catch (error) {
            refresh();
          } finally {
            this.busy = false;
            this.stepper.removeAttribute('aria-busy');
          }
        }
      }
    );
  }

  if (!customElements.get('spx-card-badge')) {
    customElements.define(
      'spx-card-badge',
      class SpxCardBadge extends HTMLElement {
        connectedCallback() {
          this.badge = this.querySelector('[data-badge]');
          this.onCart = () => this.render();
          document.addEventListener('spx:cart', this.onCart);
          this.render();
        }

        disconnectedCallback() {
          document.removeEventListener('spx:cart', this.onCart);
        }

        render() {
          const quantity = cartState.products.get(this.dataset.productId) || 0;
          this.badge.hidden = quantity === 0;
          this.badge.textContent = quantity;
        }
      }
    );
  }

  /* ---------- clicking a card (outside its buttons and links) opens the product ---------- */
  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    const card = event.target.closest('.product-card-wrapper');
    if (!card) return;
    if (event.target.closest('a, button, input, select, textarea, label, spx-card-add, modal-opener, quick-add-modal, .quick-add')) return;
    const link = card.querySelector('.card__information a.full-unstyled-link[href]') || card.querySelector('a.full-unstyled-link[href]');
    if (!link) return;
    if (event.metaKey || event.ctrlKey) window.open(link.href, '_blank', 'noopener');
    else window.location.assign(link.href);
  });

  /* ---------- keep in sync with the rest of the theme ---------- */
  refresh();
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'spx-card') return;
      refresh();
    });
  }
})();
